import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useMemo, useState, useCallback } from 'react'
import {
  Plus,
  Search,
  Filter,
  User,
  Clock,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import { cn } from '@/lib/utils.ts'
import { useAnonymize } from '@/hooks/useAnonymize.ts'
import {
  Badge,
  Button,
  DataTable,
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownTrigger,
  ErrorState,
  FilterBar,
  FilterChip,
  IconButton,
  Input,
  PageHeader,
  PriorityBadge,
  StatusBadge,
  dateColumn,
  textColumn,
  type ColumnDef,
} from '@/components/ui'
import type { Priority } from '@/styles/tokens.ts'

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'new', label: 'Neu' },
  { value: 'in_progress', label: 'In Bearbeitung' },
  { value: 'waiting_customer', label: 'Wartet auf Kunde' },
  { value: 'waiting_goods', label: 'Wartet auf Ware' },
  { value: 'waiting_appointment', label: 'Wartet auf Termin' },
  { value: 'resolved', label: 'Gelöst' },
  { value: 'closed', label: 'Geschlossen' },
]

const PRIORITY_OPTIONS: { value: Priority; label: string }[] = [
  { value: 'critical', label: 'Kritisch' },
  { value: 'high', label: 'Hoch' },
  { value: 'medium', label: 'Mittel' },
  { value: 'low', label: 'Niedrig' },
]

interface Ticket {
  id: string
  tanssId: string
  subject: string
  status: string
  priority: Priority
  dueDate?: string
  assignedUserId?: string
  customerId?: string
  companyName?: string
  tenantCode?: string
  createdAt: string
}

export function TicketsPage() {
  const a = useAnonymize()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()

  const search = searchParams.get('search') ?? ''
  const statusFilter = searchParams.get('status') ?? ''
  const priorityFilter = searchParams.get('priority') ?? ''
  const page = parseInt(searchParams.get('page') ?? '1')
  const quickFilter = searchParams.get('quick') ?? 'mine'
  const pageSize = parseInt(searchParams.get('pageSize') ?? '50')

  const [showFilters, setShowFilters] = useState(!!(statusFilter || priorityFilter))
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [searchInput, setSearchInput] = useState(search)

  const queryParams = new URLSearchParams()
  if (search) queryParams.set('search', search)
  if (statusFilter) queryParams.set('status', statusFilter)
  if (priorityFilter) queryParams.set('priority', priorityFilter)
  if (quickFilter) queryParams.set('quick', quickFilter)
  queryParams.set('page', String(page))
  queryParams.set('pageSize', String(pageSize))

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['tickets', search, statusFilter, priorityFilter, quickFilter, page, pageSize],
    queryFn: () => apiClient.get(`/tickets?${queryParams.toString()}`).then((r) => r.data),
  })

  const tickets: Ticket[] = data?.data ?? []
  const total = data?.total ?? 0
  const totalPages = Math.ceil(total / pageSize)

  const updateFilter = useCallback(
    (key: string, value: string) => {
      const params = new URLSearchParams(searchParams)
      if (value) params.set(key, value)
      else params.delete(key)
      params.set('page', '1')
      setSearchParams(params, { replace: true })
    },
    [searchParams, setSearchParams],
  )

  const handleSearch = useCallback(() => {
    updateFilter('search', searchInput)
  }, [searchInput, updateFilter])

  const clearFilters = useCallback(() => {
    setSearchParams({}, { replace: true })
    setSearchInput('')
  }, [setSearchParams])

  const bulkMutation = useMutation({
    mutationFn: (payload: {
      ticketIds: string[]
      status?: string
      priority?: Priority
      assignedUserId?: string | null
    }) => apiClient.patch('/tickets/bulk', payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tickets'] })
      setSelectedIds([])
    },
  })

  const setQuickFilter = useCallback(
    (value: string) => {
      const params = new URLSearchParams(searchParams)
      if (params.get('quick') === value) params.delete('quick')
      else params.set('quick', value)
      params.set('page', '1')
      setSearchParams(params, { replace: true })
    },
    [searchParams, setSearchParams],
  )

  const toggleStatusValue = (value: string) => {
    const current = statusFilter ? statusFilter.split(',') : []
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value]
    updateFilter('status', next.join(','))
  }

  const togglePriorityValue = (value: string) => {
    const current = priorityFilter ? priorityFilter.split(',') : []
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value]
    updateFilter('priority', next.join(','))
  }

  const hasActiveFilters = !!(statusFilter || priorityFilter || search || quickFilter)
  const activeFilterCount = [statusFilter, priorityFilter, search].filter(Boolean).length

  const columns: ColumnDef<Ticket, unknown>[] = useMemo(
    () => [
      {
        accessorKey: 'tanssId',
        header: '#',
        size: 80,
        cell: ({ getValue }) => (
          <span className="font-mono text-caption text-gray-500">
            {(getValue() as string) ?? ''}
          </span>
        ),
      },
      {
        accessorKey: 'subject',
        header: 'Betreff',
        cell: ({ getValue }) => (
          <span className="font-medium text-gray-900">{getValue() as string}</span>
        ),
      },
      {
        accessorKey: 'companyName',
        header: 'Firma',
        cell: ({ row }) => {
          const raw = row.original.companyName
          return (
            <span className="text-small text-gray-600 truncate max-w-[200px] block" title={raw ? a.name(raw) : ''}>
              {raw ? a.name(raw) : '–'}
            </span>
          )
        },
      },
      {
        accessorKey: 'tenantCode',
        header: 'Zuweisung',
        size: 120,
        cell: ({ getValue }) => {
          const code = getValue() as string | undefined
          if (!code) return <span className="text-caption text-gray-400">–</span>
          return <Badge variant="info">{code}</Badge>
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 150,
        cell: ({ getValue }) => <StatusBadge status={getValue() as string} />,
      },
      {
        accessorKey: 'priority',
        header: 'Priorität',
        size: 120,
        cell: ({ getValue }) => {
          const p = getValue() as Priority | null | undefined
          return p ? <PriorityBadge priority={p} /> : '–'
        },
      },
      dateColumn<Ticket>('dueDate', 'Fälligkeit', { size: 120 }),
      textColumn<Ticket>('createdAt', 'Erstellt', { size: 0 }),
    ],
    [a],
  )

  if (isError) {
    return (
      <div className="space-y-4">
        <PageHeader title="Tickets" />
        <ErrorState
          title="Tickets konnten nicht geladen werden"
          description={
            ((error as { response?: { data?: { error?: string } } })?.response?.data?.error ??
              (error as Error)?.message) ||
            'TANSS nicht erreichbar'
          }
          details={error ? JSON.stringify(error, Object.getOwnPropertyNames(error), 2) : undefined}
          onRetry={() => refetch()}
        />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Tickets"
        description={total > 0 ? `${total} Tickets gefunden` : undefined}
        actions={<Button leadingIcon={<Plus />}>Neues Ticket</Button>}
      />

      {/* Search + Filter Toggle */}
      <div className="flex items-center gap-2">
        <Input
          wrapperClassName="flex-1"
          leadingIcon={<Search />}
          placeholder="Tickets durchsuchen …"
          value={searchInput}
          onChange={(e) => setSearchInput(e.currentTarget.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
        />
        <Button
          variant={showFilters || hasActiveFilters ? 'primary' : 'secondary'}
          leadingIcon={<Filter />}
          onClick={() => setShowFilters((v) => !v)}
        >
          Filter
          {activeFilterCount > 0 ? (
            <Badge variant="brand" size="sm" className="ml-1">
              {activeFilterCount}
            </Badge>
          ) : null}
        </Button>
        {hasActiveFilters ? (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            Zurücksetzen
          </Button>
        ) : null}
      </div>

      {/* Quick Filters */}
      <FilterBar>
        <FilterChip
          active={quickFilter === 'mine'}
          onClick={() => setQuickFilter('mine')}
          leadingIcon={<User />}
        >
          Meine Tickets
        </FilterChip>
        <FilterChip
          active={quickFilter === 'open'}
          onClick={() => setQuickFilter('open')}
          leadingIcon={<Clock />}
        >
          Offen
        </FilterChip>
        <FilterChip
          active={quickFilter === 'overdue'}
          onClick={() => setQuickFilter('overdue')}
          leadingIcon={<AlertTriangle />}
        >
          Überfällig
        </FilterChip>
        <FilterChip
          active={quickFilter === 'unassigned'}
          onClick={() => setQuickFilter('unassigned')}
          leadingIcon={<User />}
        >
          Nicht zugewiesen
        </FilterChip>
      </FilterBar>

      {/* Detail-Filter */}
      {showFilters ? (
        <div className="bg-white rounded-lg border border-gray-200 shadow-card p-4 flex flex-wrap gap-6">
          <div>
            <div className="text-caption font-semibold uppercase tracking-wide text-gray-500 mb-2">
              Status
            </div>
            <div className="flex flex-wrap gap-1.5">
              {STATUS_OPTIONS.map((opt) => {
                const selected = statusFilter.split(',').includes(opt.value)
                return (
                  <FilterChip
                    key={opt.value}
                    active={selected}
                    onClick={() => toggleStatusValue(opt.value)}
                  >
                    {opt.label}
                  </FilterChip>
                )
              })}
            </div>
          </div>
          <div>
            <div className="text-caption font-semibold uppercase tracking-wide text-gray-500 mb-2">
              Priorität
            </div>
            <div className="flex flex-wrap gap-1.5">
              {PRIORITY_OPTIONS.map((opt) => {
                const selected = priorityFilter.split(',').includes(opt.value)
                return (
                  <FilterChip
                    key={opt.value}
                    active={selected}
                    onClick={() => togglePriorityValue(opt.value)}
                  >
                    {opt.label}
                  </FilterChip>
                )
              })}
            </div>
          </div>
        </div>
      ) : null}

      {/* Bulk-Actions */}
      {selectedIds.length > 0 ? (
        <div className="bg-brand-50 border border-brand-200 rounded-lg px-4 py-2.5 flex items-center gap-3">
          <span className="text-small font-medium text-brand-700">
            {selectedIds.length} ausgewählt
          </span>
          <div className="flex items-center gap-2 ml-auto">
            <Dropdown>
              <DropdownTrigger asChild>
                <Button size="sm" variant="secondary">Status ändern ▾</Button>
              </DropdownTrigger>
              <DropdownContent align="end">
                {STATUS_OPTIONS.map((s) => (
                  <DropdownItem
                    key={s.value}
                    onSelect={() =>
                      bulkMutation.mutate({ ticketIds: selectedIds, status: s.value })
                    }
                  >
                    {s.label}
                  </DropdownItem>
                ))}
              </DropdownContent>
            </Dropdown>
            <Dropdown>
              <DropdownTrigger asChild>
                <Button size="sm" variant="secondary">Priorität ändern ▾</Button>
              </DropdownTrigger>
              <DropdownContent align="end">
                {PRIORITY_OPTIONS.map((p) => (
                  <DropdownItem
                    key={p.value}
                    onSelect={() =>
                      bulkMutation.mutate({ ticketIds: selectedIds, priority: p.value })
                    }
                  >
                    {p.label}
                  </DropdownItem>
                ))}
              </DropdownContent>
            </Dropdown>
            <Button size="sm" variant="ghost" onClick={() => setSelectedIds([])}>
              Auswahl aufheben
            </Button>
          </div>
        </div>
      ) : null}

      {/* Tabelle */}
      <DataTable<Ticket>
        data={tickets}
        columns={columns}
        getRowId={(t) => t.id}
        loading={isLoading}
        enableRowSelection
        selectedRowIds={selectedIds}
        onSelectedRowIdsChange={setSelectedIds}
        onRowClick={(t) => navigate(`/tickets/${t.id}`)}
      />

      {/* Server-Pagination */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-caption text-gray-500">
            Seite {page} von {Math.max(1, totalPages)}
          </span>
          <div className="flex items-center gap-1.5">
            <span className="text-caption text-gray-500">Einträge:</span>
            <select
              value={pageSize}
              onChange={(e) => {
                const params = new URLSearchParams(searchParams)
                params.set('pageSize', e.target.value)
                params.set('page', '1')
                setSearchParams(params, { replace: true })
              }}
              className="text-small border border-gray-300 rounded px-1.5 py-1 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
            >
              {[50, 100, 250].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        </div>
        {totalPages > 1 ? (
          <div className="flex items-center gap-1">
            <IconButton
              size="sm"
              icon={<ChevronLeft />}
              aria-label="Vorige Seite"
              disabled={page <= 1}
              onClick={() => updateFilter('page', String(page - 1))}
            />
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i
              if (p > totalPages) return null
              return (
                <button
                  key={p}
                  onClick={() => updateFilter('page', String(p))}
                  className={cn(
                    'w-8 h-8 rounded text-small font-medium transition-colors',
                    p === page ? 'bg-brand-700 text-white' : 'text-gray-600 hover:bg-gray-100',
                  )}
                >
                  {p}
                </button>
              )
            })}
            <IconButton
              size="sm"
              icon={<ChevronRight />}
              aria-label="Nächste Seite"
              disabled={page >= totalPages}
              onClick={() => updateFilter('page', String(page + 1))}
            />
          </div>
        ) : null}
      </div>
    </div>
  )
}
